import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'

// Brukes i to scenarier:
//  1) Fra e-post-lenken (PASSWORD_RECOVERY-event) — bruker er logget inn
//     i en midlertidig recovery-session og må sette nytt passord.
//  2) Fra Settings/bruker-menyen når innlogget bruker vil bytte passord.
//
// Sessionen er allerede satt av Supabase; vi trenger bare å kalle
// updateUser({ password }).
export default function ResetPassword() {
  const navigate = useNavigate()
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)
  const [hasSession, setHasSession] = useState(null)

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setHasSession(!!session)
    })
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)

    if (password.length < 8) {
      setError('Passord må være minst 8 tegn')
      return
    }
    if (password !== confirm) {
      setError('Passordene er ikke like')
      return
    }

    setLoading(true)
    const { error } = await supabase.auth.updateUser({ password })
    setLoading(false)

    if (error) {
      setError(error.message || 'Kunne ikke oppdatere passord')
      return
    }
    setSuccess(true)
    // Gi brukeren et sekund til å lese suksess-meldingen før vi sender
    // videre. Hvis de kom hit fra en recovery-lenke er de nå fullt
    // innlogget og havner på oversikten.
    setTimeout(() => navigate('/', { replace: true }), 1200)
  }

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-text">Pulse</h1>
          <p className="text-text-muted text-sm mt-1">Sett nytt passord</p>
        </div>

        <div className="bg-card rounded-xl border border-border p-6">
          {hasSession === false ? (
            <div className="text-center py-4">
              <p className="text-text-muted text-sm">
                Lenken er utløpt eller ugyldig. Be om en ny tilbakestillings-lenke fra innloggingssiden.
              </p>
              <button
                onClick={() => navigate('/', { replace: true })}
                className="mt-4 text-sm text-primary hover:text-primary-light"
              >
                Tilbake til innlogging
              </button>
            </div>
          ) : success ? (
            <div className="text-center py-4">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-green-500/10 flex items-center justify-center">
                <svg className="w-6 h-6 text-green-400" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
              </div>
              <p className="text-text">Passord oppdatert — sender deg videre …</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <label className="block text-sm font-medium text-text-muted mb-1.5">Nytt passord</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                minLength={8}
                autoFocus
                autoComplete="new-password"
                className="w-full bg-surface border border-border rounded-lg px-3 py-2.5 text-text focus:outline-none focus:border-primary transition-colors"
              />

              <label className="block text-sm font-medium text-text-muted mt-4 mb-1.5">Bekreft passord</label>
              <input
                type="password"
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                className="w-full bg-surface border border-border rounded-lg px-3 py-2.5 text-text focus:outline-none focus:border-primary transition-colors"
              />

              {error && <p className="mt-2 text-sm text-red-400">{error}</p>}

              <button
                type="submit"
                disabled={loading || !password || !confirm}
                className="w-full mt-4 py-2.5 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-colors disabled:opacity-50"
              >
                {loading ? 'Lagrer...' : 'Lagre passord'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
